import React from 'react';
import * as Sentry from '@sentry/nextjs';
import { NextRouter, withRouter } from 'next/router';
import { isOnline } from '../../utils/environment';
import Error from './Error/Error';

type ErrorBoundaryProps = {
    errorComponent?: typeof React.Component;
    router: NextRouter;
};

type ErrorBoundaryState = {
    hasError: boolean;
    prevPath: string | null;
};

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props) {
        super(props);

        this.state = {
            hasError: false,
            prevPath: null,
        };
    }

    static getDerivedStateFromProps(
        nextProps: ErrorBoundaryProps,
        prevState: ErrorBoundaryState
    ): ErrorBoundaryState {
        const { hasError, prevPath } = prevState;
        const { router } = nextProps;

        // reset previous error when navigating
        return {
            hasError: hasError && router.asPath !== prevPath ? false : prevState.hasError,
            prevPath: router.asPath,
        };
    }

    componentDidCatch(error) {
        this.setState({ hasError: true });
        Sentry.captureException(error);
    }

    render() {
        const { hasError } = this.state;
        const { errorComponent, router, children } = this.props;

        if (hasError) {
            const Component = errorComponent || Error;

            return <Component pathname={router.asPath} isNetworkOnline={isOnline()} />;
        }

        return children;
    }
}

export default withRouter(ErrorBoundary);
